import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ArrowLeft, Calendar, Lock, Feather } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/i18n/LanguageContext';
import type { TreslogEntry } from '@/types/treslog';

export default function TreslogPublicView() {
  const { id } = useParams<{ id: string }>();
  const { language } = useLanguage();
  const [entry, setEntry] = useState<TreslogEntry | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const content = {
    en: {
      back: 'Back',
      notFound: 'Entry not found',
      notFoundDesc: 'This entry may be private or does not exist.',
      private: 'This entry is private',
      privateDesc: 'The author has chosen to keep this reflection private.',
      goHome: 'Go to Homepage',
      writtenOn: 'Written on',
      poweredBy: 'A private reflection powered by',
    },
    es: {
      back: 'Volver',
      notFound: 'Entrada no encontrada',
      notFoundDesc: 'Esta entrada puede ser privada o no existe.',
      private: 'Esta entrada es privada',
      privateDesc: 'El autor ha decidido mantener esta reflexión privada.',
      goHome: 'Ir al Inicio',
      writtenOn: 'Escrito el',
      poweredBy: 'Una reflexión privada impulsada por',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  useEffect(() => {
    const fetchEntry = async () => {
      if (!id) {
        setError('not_found');
        setIsLoading(false);
        return;
      }

      const { data, error: fetchError } = await (supabase as any)
        .from('treslog_entries')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError || !data) {
        setError('not_found');
      } else if (!data.is_public) {
        setError('private');
      } else {
        setEntry(data as TreslogEntry);
      }
      setIsLoading(false);
    };

    fetchEntry();
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-muted-foreground"
        >
          <Feather className="h-8 w-8 animate-pulse" />
        </motion.div>
      </div>
    );
  }

  if (error === 'private') {
    return (
      <div className="min-h-screen bg-background">
        <div className="container max-w-2xl py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted">
              <Lock className="h-10 w-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{t.private}</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              {t.privateDesc}
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t.goHome}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (error === 'not_found' || !entry) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container max-w-2xl py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-muted">
              <Feather className="h-10 w-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">{t.notFound}</h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              {t.notFoundDesc}
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t.goHome}
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Subtle header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="container max-w-3xl py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-4 w-4" />
              {t.back}
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Feather className="h-4 w-4" />
            <span className="text-sm font-medium">TRESLOG</span>
          </div>
        </div>
      </header>

      {/* Journal content */}
      <main className="container max-w-3xl py-12 px-6">
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Date */}
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Calendar className="h-4 w-4" />
            <time dateTime={entry.created_at}>
              {t.writtenOn} {format(new Date(entry.created_at), 'MMMM d, yyyy')}
            </time>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground leading-tight">
            {entry.title}
          </h1>

          {/* Decorative divider */}
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-border" />
            <Feather className="h-4 w-4 text-primary/50" />
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Photos gallery */}
          {entry.photo_urls && entry.photo_urls.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`grid gap-4 ${
                entry.photo_urls.length === 1
                  ? 'grid-cols-1'
                  : entry.photo_urls.length === 2
                  ? 'grid-cols-2'
                  : 'grid-cols-2 md:grid-cols-3'
              }`}
            >
              {entry.photo_urls.map((url, index) => (
                <div
                  key={index}
                  className={`relative overflow-hidden rounded-lg ${
                    entry.photo_urls!.length === 1 ? 'aspect-video' : 'aspect-square'
                  }`}
                >
                  <img
                    src={url}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </motion.div>
          )}

          {/* Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="prose prose-lg dark:prose-invert max-w-none"
          >
            <div className="text-foreground/90 leading-relaxed text-lg md:text-xl whitespace-pre-wrap font-serif">
              {entry.content}
            </div>
          </motion.div>

          {/* Footer divider */}
          <div className="pt-8">
            <div className="flex items-center gap-4">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground uppercase tracking-widest">
                ✦
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
          </div>
        </motion.article>
      </main>

      {/* Subtle footer */}
      <footer className="border-t border-border/50 py-8">
        <div className="container max-w-3xl text-center">
          <p className="text-sm text-muted-foreground">
            {t.poweredBy}{' '}
            <Link to="/" className="text-primary hover:underline font-medium">
              Say It NOW
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}
